import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { diffLines } from "@/lib/diff";
import { sanitizeDjenText } from "@/lib/djenText";
import { CourtLabel } from "@/components/CourtLabel";
import { TopBar } from "@/components/PageShell";
import {
  ArrowLeft, ArrowRightLeft, Columns, GitCompare, BarChart3, ExternalLink,
  Loader2, FileText, Clock, CheckCircle2, Sparkles, AlignLeft, Wand2, Save, X,
  BookMarked, Copy, ClipboardList,
} from "lucide-react";

const NAVY = "#0E1D2D";
const GOLD = "#C9953D";

const STATUS_META = {
  AGUARDANDO_REVISAO: { label: "Aguardando revisão", tone: "amber", icon: Clock },
  EM_REVISAO: { label: "Em revisão", tone: "blue", icon: Clock },
  REVISADO: { label: "Revisado", tone: "emerald", icon: CheckCircle2 },
  PROTOCOLADO: { label: "Protocolado", tone: "slate", icon: CheckCircle2 },
};
const TONE = {
  amber: "bg-amber-100 text-amber-900 border-amber-200",
  blue: "bg-blue-100 text-blue-900 border-blue-200",
  emerald: "bg-emerald-100 text-emerald-900 border-emerald-200",
  slate: "bg-slate-100 text-slate-800 border-slate-200",
};
const fmtDT = (s) => s
  ? new Date(s).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
  : "—";

// -----------------------------------------------------------------------------
// Página principal
// -----------------------------------------------------------------------------
export default function PreparadosCompare() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const idA = params.get("a");
  const idB = params.get("b");
  const [docA, setDocA] = useState(null);
  const [docB, setDocB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("side");   // side | diff | metrics
  const [swap, setSwap] = useState(false);

  useEffect(() => {
    if (!idA || !idB) {
      toast.error("Selecione dois preparados para comparar");
      navigate("/preparados", { replace: true });
      return;
    }
    if (idA === idB) {
      toast.error("Selecione dois preparados diferentes");
      navigate("/preparados", { replace: true });
      return;
    }
    let stop = false;
    Promise.all([
      api.get(`/prepared-documents/${idA}`),
      api.get(`/prepared-documents/${idB}`),
    ])
      .then(([a, b]) => {
        if (stop) return;
        setDocA(a.data); setDocB(b.data);
      })
      .catch(() => toast.error("Falha ao carregar preparados"))
      .finally(() => !stop && setLoading(false));
    return () => { stop = true; };
  }, [idA, idB, navigate]);

  const left  = swap ? docB : docA;
  const right = swap ? docA : docB;

  // IA de Convergência state
  const [convOpen, setConvOpen] = useState(false);
  const [convBusy, setConvBusy] = useState(false);
  const [convData, setConvData] = useState(null);   // {preview, coverage_pct, stats, ...}
  const [convError, setConvError] = useState("");

  const runConverge = async () => {
    if (!left || !right) return;
    setConvBusy(true); setConvError(""); setConvData(null); setConvOpen(true);
    try {
      const r = await api.post("/prepared-documents/converge", {
        a_id: left.id, b_id: right.id,
      });
      setConvData(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Falha na análise";
      setConvError(typeof detail === "string" ? detail : JSON.stringify(detail));
    } finally {
      setConvBusy(false);
    }
  };

  const diff = useMemo(() => {
    if (!left || !right) return null;
    return diffLines(left.content_markdown || "", right.content_markdown || "");
  }, [left, right]);

  const similarity = useMemo(() => {
    if (!diff) return 0;
    const total = diff.stats.unchanged + diff.stats.additions + diff.stats.deletions;
    if (!total) return 0;
    return Math.round((diff.stats.unchanged / total) * 100);
  }, [diff]);

  const insights = useMemo(() => {
    if (!left || !right) return [];
    const out = [];
    const norm = (s) => (s || "").toString().trim().toLowerCase();

    if (norm(left.tribunal) && norm(left.tribunal) === norm(right.tribunal)) {
      out.push({ tone: "emerald", text: `Mesmo tribunal: ${left.tribunal}` });
    } else if (left.tribunal || right.tribunal) {
      out.push({ tone: "amber", text: `Tribunais diferentes: ${left.tribunal || "—"} vs ${right.tribunal || "—"}` });
    }

    if (norm(left.document_type) === norm(right.document_type) && left.document_type) {
      out.push({ tone: "emerald", text: `Mesmo tipo: ${left.document_type}` });
    } else if (left.document_type || right.document_type) {
      out.push({ tone: "slate", text: `Tipos diferentes: ${left.document_type || "—"} vs ${right.document_type || "—"}` });
    }

    if (norm(left.process_number) && norm(left.process_number) === norm(right.process_number)) {
      out.push({ tone: "sky", text: `Mesmo processo (${left.process_number})` });
    }

    if (similarity >= 85) out.push({ tone: "emerald", text: `Alta similaridade textual (${similarity}%) — provável reuso de peça` });
    else if (similarity >= 50) out.push({ tone: "amber", text: `Similaridade moderada (${similarity}%) — verificar convergências` });
    else out.push({ tone: "slate", text: `Baixa similaridade textual (${similarity}%) — peças distintas` });

    return out;
  }, [left, right, similarity]);

  if (loading) {
    return (
      <div className="flex-1 min-w-0 flex flex-col">
        <TopBar/>
        <div className="flex justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400"/>
        </div>
      </div>
    );
  }

  if (!left || !right) return null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="preparados-compare-page">
      <TopBar/>
      <div className="px-6 lg:px-10 py-6 max-w-[1400px] mx-auto">
        <div className="flex items-center gap-3 mb-4">
          <Link to="/preparados" data-testid="compare-back"
            className="h-9 px-3 rounded-lg border border-slate-200 bg-white text-slate-700 text-sm inline-flex items-center gap-1.5 hover:border-slate-300">
            <ArrowLeft className="w-4 h-4"/> Voltar
          </Link>
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] font-bold" style={{ color: GOLD }}>
              <GitCompare className="w-3 h-3 inline mr-1"/> Comparar Preparados
            </div>
            <h1 className="font-heading text-2xl mt-0.5" style={{ color: NAVY }}>
              Duas peças lado a lado
            </h1>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={runConverge}
              disabled={convBusy}
              data-testid="compare-converge-btn"
              className="h-9 px-3.5 rounded-lg text-white text-[13px] font-semibold inline-flex items-center gap-1.5 hover:opacity-95 disabled:opacity-60"
              style={{ background: GOLD }}>
              {convBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Wand2 className="w-3.5 h-3.5"/>}
              Gerar template com IA
            </button>
            <button onClick={() => setSwap(s => !s)} data-testid="compare-swap"
              title="Inverter A e B"
              className="h-9 px-3 rounded-lg border border-slate-200 bg-white text-slate-700 text-sm inline-flex items-center gap-1.5 hover:border-slate-300">
              <ArrowRightLeft className="w-3.5 h-3.5"/> Trocar
            </button>
          </div>
        </div>

        {/* Barra de similaridade + insights */}
        <SimilarityBar percent={similarity} stats={diff?.stats} insights={insights}/>

        {/* Modo de visualização */}
        <div className="flex items-center gap-2 mb-3 mt-4">
          <span className="text-[11px] uppercase tracking-widest text-slate-500 mr-1">Visualização</span>
          {[
            { key: "side", label: "Lado a lado", icon: Columns },
            { key: "diff", label: "Diff", icon: GitCompare },
            { key: "metrics", label: "Métricas", icon: BarChart3 },
          ].map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => setMode(key)}
              data-testid={`compare-mode-${key}`}
              className={`h-8 px-3 rounded-full text-[12px] font-semibold border inline-flex items-center gap-1.5 transition ${
                mode === key
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
              }`}>
              <Icon className="w-3.5 h-3.5"/> {label}
            </button>
          ))}
        </div>

        {mode === "side" && <SideBySideView a={left} b={right}/>}
        {mode === "diff" && <DiffView diff={diff}/>}
        {mode === "metrics" && <MetricsView a={left} b={right} stats={diff?.stats}/>}
      </div>

      {convOpen && (
        <ConvergeDrawer
          busy={convBusy}
          data={convData}
          error={convError}
          onClose={() => { setConvOpen(false); setConvData(null); setConvError(""); }}
          onRerun={runConverge}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
function SimilarityBar({ percent, stats, insights }) {
  const barColor =
    percent >= 85 ? "bg-emerald-500" :
    percent >= 50 ? "bg-amber-500"   :
    "bg-slate-400";
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="compare-similarity">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-baseline justify-between mb-1">
            <span className="text-[10.5px] uppercase tracking-widest text-slate-500">Similaridade textual</span>
            <span className="font-heading text-xl font-semibold" style={{ color: NAVY }} data-testid="compare-similarity-pct">
              {percent}%
            </span>
          </div>
          <div className="h-2.5 w-full rounded-full bg-slate-100 overflow-hidden">
            <div className={`h-full transition-all ${barColor}`} style={{ width: `${percent}%` }}/>
          </div>
          {stats && (
            <div className="mt-1.5 flex gap-3 text-[10.5px] text-slate-500">
              <span className="text-slate-700"><b>{stats.unchanged}</b> iguais</span>
              <span className="text-emerald-700"><b>+{stats.additions}</b> em B</span>
              <span className="text-rose-700"><b>−{stats.deletions}</b> em A</span>
            </div>
          )}
        </div>
        {insights?.length > 0 && (
          <div className="flex-1 min-w-[280px] flex flex-wrap gap-1.5">
            {insights.map((i, idx) => (
              <span key={idx} data-testid={`compare-insight-${idx}`}
                className={`text-[11px] px-2 py-1 rounded-md border inline-flex items-center gap-1 ${
                  i.tone === "emerald" ? "bg-emerald-50 text-emerald-800 border-emerald-200" :
                  i.tone === "amber"   ? "bg-amber-50 text-amber-800 border-amber-200"       :
                  i.tone === "sky"     ? "bg-sky-50 text-sky-800 border-sky-200"             :
                  "bg-slate-50 text-slate-700 border-slate-200"
                }`}>
                <Sparkles className="w-3 h-3"/> {i.text}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
function DocHeader({ doc, side }) {
  const meta = STATUS_META[doc.status] || STATUS_META.AGUARDANDO_REVISAO;
  return (
    <div className="p-3 border-b border-slate-100 bg-slate-50" data-testid={`compare-header-${side}`}>
      <div className="flex items-center gap-1.5 flex-wrap mb-1">
        <span className="text-[9px] font-bold uppercase tracking-widest text-white px-1.5 py-0.5 rounded"
              style={{ background: NAVY }}>
          Peça {side}
        </span>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold border ${TONE[meta.tone]}`}>
          <meta.icon className="w-3 h-3"/> {meta.label}
        </span>
        {doc.tribunal && <CourtLabel raw={doc.tribunal} size="xs" variant="chip"/>}
      </div>
      <Link to={`/preparado/${doc.id}`} data-testid={`compare-open-${side}`}
        className="font-semibold text-slate-900 hover:underline inline-flex items-baseline gap-1"
        style={{ color: NAVY }}>
        <FileText className="w-3.5 h-3.5 self-center text-slate-400"/>
        <span className="truncate">{sanitizeDjenText(doc.title || doc.document_type || "Preparado")}</span>
        <ExternalLink className="w-3 h-3 opacity-60"/>
      </Link>
      <div className="text-[11px] text-slate-500 mt-0.5 flex gap-3 flex-wrap">
        {doc.process_number && <span className="font-mono">{doc.process_number}</span>}
        <span>Criado {fmtDT(doc.created_at)}</span>
      </div>
    </div>
  );
}

function SideBySideView({ a, b }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="compare-sidebyside">
      {[["A", a], ["B", b]].map(([side, doc]) => (
        <div key={side} className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <DocHeader doc={doc} side={side}/>
          <pre className="p-4 text-[12.5px] leading-relaxed text-slate-800 whitespace-pre-wrap font-mono max-h-[68vh] overflow-y-auto"
               data-testid={`compare-body-${side}`}>
            {doc.content_markdown || "(vazio)"}
          </pre>
        </div>
      ))}
    </div>
  );
}

function DiffView({ diff }) {
  if (!diff) return null;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="compare-diff">
      <div className="px-4 py-2 border-b border-slate-100 bg-slate-50 flex items-center gap-3 flex-wrap">
        <AlignLeft className="w-4 h-4 text-slate-500"/>
        <span className="text-[11px] uppercase tracking-widest text-slate-500">
          Diff linha a linha · A → B
        </span>
        <span className="text-[10.5px] text-slate-500 ml-auto flex gap-3">
          <span className="text-emerald-700"><b>+{diff.stats.additions}</b> em B</span>
          <span className="text-rose-700"><b>−{diff.stats.deletions}</b> em A</span>
          <span className="text-slate-700"><b>={diff.stats.unchanged}</b> iguais</span>
        </span>
      </div>
      <div className="max-h-[70vh] overflow-y-auto font-mono text-[12.5px] leading-relaxed">
        {diff.lines.map((l, i) => (
          <div key={i} className={
            l.type === "add" ? "bg-emerald-50 text-emerald-900 border-l-2 border-emerald-400 pl-3 pr-2 py-0.5" :
            l.type === "del" ? "bg-rose-50 text-rose-900 border-l-2 border-rose-400 pl-3 pr-2 py-0.5 line-through decoration-rose-300" :
            "text-slate-700 pl-3 pr-2 py-0.5"
          }>
            <span className="select-none mr-2 text-slate-400">
              {l.type === "add" ? "+" : l.type === "del" ? "−" : " "}
            </span>
            {l.text || "\u00A0"}
          </div>
        ))}
      </div>
    </div>
  );
}

function MetricsView({ a, b, stats }) {
  const wc = (s) => (s || "").split(/\s+/).filter(Boolean).length;
  const cc = (s) => (s || "").length;
  const lc = (s) => (s || "").split("\n").length;
  const rows = [
    { label: "Título", a: a.title || a.document_type, b: b.title || b.document_type },
    { label: "Status", a: STATUS_META[a.status]?.label || a.status, b: STATUS_META[b.status]?.label || b.status },
    { label: "Tribunal", a: a.tribunal || "—", b: b.tribunal || "—" },
    { label: "Órgão", a: sanitizeDjenText(a.orgao || "—"), b: sanitizeDjenText(b.orgao || "—") },
    { label: "Processo", a: a.process_number || "—", b: b.process_number || "—" },
    { label: "Tipo", a: a.document_type || "—", b: b.document_type || "—" },
    { label: "Origem", a: a.origin || "—", b: b.origin || "—" },
    { label: "Criado em", a: fmtDT(a.created_at), b: fmtDT(b.created_at) },
    { label: "Atualizado em", a: fmtDT(a.updated_at), b: fmtDT(b.updated_at) },
    { label: "Linhas de conteúdo", a: lc(a.content_markdown), b: lc(b.content_markdown) },
    { label: "Palavras", a: wc(a.content_markdown), b: wc(b.content_markdown) },
    { label: "Caracteres", a: cc(a.content_markdown), b: cc(b.content_markdown) },
  ];
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="compare-metrics">
      <table className="w-full text-sm">
        <thead className="bg-slate-50">
          <tr className="text-[10.5px] uppercase tracking-widest text-slate-500">
            <th className="text-left px-4 py-2 w-40">Métrica</th>
            <th className="text-left px-4 py-2">Peça A</th>
            <th className="text-left px-4 py-2">Peça B</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const equal = String(r.a) === String(r.b);
            return (
              <tr key={r.label} className="border-t border-slate-100"
                  data-testid={`compare-metric-${r.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <td className="px-4 py-2 font-semibold text-slate-600 text-[12px]">{r.label}</td>
                <td className={`px-4 py-2 ${equal ? "text-slate-700" : "text-slate-900 font-medium"}`}>{r.a}</td>
                <td className={`px-4 py-2 ${equal ? "text-slate-700" : "text-slate-900 font-medium"}`}>
                  <div className="flex items-center gap-1.5">
                    <span>{r.b}</span>
                    {equal
                      ? <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200">igual</span>
                      : <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">difere</span>}
                  </div>
                </td>
              </tr>
            );
          })}
          {stats && (
            <tr className="border-t border-slate-100 bg-slate-50">
              <td className="px-4 py-2 font-semibold text-slate-700 text-[12px]">Diff textual</td>
              <td colSpan={2} className="px-4 py-2 text-[12.5px] flex gap-3 text-slate-700">
                <span className="text-slate-700"><b>{stats.unchanged}</b> iguais</span>
                <span className="text-emerald-700"><b>+{stats.additions}</b> só em B</span>
                <span className="text-rose-700"><b>−{stats.deletions}</b> só em A</span>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}


// -----------------------------------------------------------------------------
// IA de Convergência · drawer de revisão e persistência de template
// -----------------------------------------------------------------------------
function ConvergeDrawer({ busy, data, error, onClose, onRerun }) {
  const navigate = useNavigate();
  const preview = data?.preview;
  const [title, setTitle] = useState("");
  const [docType, setDocType] = useState("");
  const [content, setContent] = useState("");
  const [variables, setVariables] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!preview) return;
    setTitle(preview.title || "");
    setDocType(preview.document_type || "Outro");
    setContent(preview.content_markdown || "");
    setVariables((preview.variables || []).map((v) => ({ ...v })));
  }, [preview]);

  // Placeholders realmente presentes no template atual (recalcula ao editar)
  const usedPlaceholders = useMemo(() => {
    const found = new Set();
    const re = /\{\{\s*([A-Z0-9_]+)\s*\}\}/g;
    let m;
    while ((m = re.exec(content)) !== null) found.add(m[1]);
    return found;
  }, [content]);

  const highlightedContent = useMemo(() => {
    // simple render: split by {{X}}, wrap placeholder tokens
    const parts = content.split(/(\{\{\s*[A-Z0-9_]+\s*\}\})/g);
    return parts.map((p, i) => {
      if (/^\{\{\s*[A-Z0-9_]+\s*\}\}$/.test(p)) {
        return (
          <span key={i} className="bg-amber-100 text-amber-900 px-1 rounded font-mono text-[11.5px] font-semibold border border-amber-300 mx-0.5">
            {p}
          </span>
        );
      }
      return <span key={i}>{p}</span>;
    });
  }, [content]);

  const updateVarField = (idx, field, val) => {
    setVariables((vs) => {
      const copy = [...vs];
      copy[idx] = { ...copy[idx], [field]: val };
      return copy;
    });
  };

  const save = async () => {
    if (!title.trim()) { toast.error("Título obrigatório"); return; }
    if (title.length > 200) { toast.error("Título deve ter até 200 chars"); return; }
    setSaving(true);
    try {
      const r = await api.post("/converge-templates", {
        title: title.trim(),
        document_type: docType,
        content_markdown: content,
        variables: variables.filter(v => usedPlaceholders.has(v.name)),
        notes: preview?.notes || "",
        source: {
          kind: "converge",
          doc_a_id: data.source?.doc_a_id,
          doc_b_id: data.source?.doc_b_id,
          coverage_pct: data.coverage_pct,
        },
      });
      toast.success("Template salvo em Modelos & Cláusulas");
      onClose();
      navigate(`/modelos-converge?highlight=${r.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar template");
    } finally { setSaving(false); }
  };

  const copyMarkdown = async () => {
    try {
      await navigator.clipboard.writeText(content);
      toast.success("Markdown copiado");
    } catch { toast.error("Não foi possível copiar"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-stretch justify-end"
         onClick={onClose} data-testid="converge-drawer">
      <div className="w-full max-w-3xl bg-white h-full overflow-hidden flex flex-col shadow-2xl"
           onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-amber-50 to-white flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center"
               style={{ background: `${GOLD}22`, border: `1px solid ${GOLD}66`, color: GOLD }}>
            <Wand2 className="w-5 h-5"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.24em] font-bold" style={{ color: GOLD }}>
              IA de Convergência
            </div>
            <h2 className="font-heading text-xl font-semibold" style={{ color: NAVY }}>
              Template reutilizável
            </h2>
          </div>
          <button onClick={onClose} data-testid="converge-close"
                  className="w-8 h-8 rounded-lg hover:bg-slate-100 inline-flex items-center justify-center text-slate-500">
            <X className="w-4 h-4"/>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {busy && (
            <div className="p-10 text-center" data-testid="converge-loading">
              <Loader2 className="w-10 h-10 animate-spin text-amber-500 mx-auto mb-3"/>
              <div className="text-slate-800 font-semibold">Analisando convergências…</div>
              <p className="text-sm text-slate-500 mt-1">
                O Hermes está identificando trechos idênticos e transformando divergências
                em placeholders. Isso leva alguns segundos.
              </p>
            </div>
          )}

          {!busy && error && (
            <div className="p-6 text-center" data-testid="converge-error">
              <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-rose-50 text-rose-800 border border-rose-200">
                <ClipboardList className="w-4 h-4"/>
                <span className="text-sm">{error}</span>
              </div>
              <div className="mt-4">
                <button onClick={onRerun}
                        className="h-9 px-4 rounded-lg text-white text-sm font-semibold hover:opacity-95"
                        style={{ background: NAVY }}>
                  Tentar novamente
                </button>
              </div>
            </div>
          )}

          {!busy && !error && preview && (
            <div className="p-5 space-y-5">
              {/* Stats banner */}
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 flex items-center gap-4 flex-wrap"
                   data-testid="converge-stats">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4" style={{ color: GOLD }}/>
                  <span className="text-[12px] font-semibold text-amber-900">
                    Cobertura: <b>{data.coverage_pct}%</b>
                  </span>
                </div>
                <span className="text-[12px] text-amber-900">
                  <b>{data.common_blocks_count}</b> bloco{data.common_blocks_count !== 1 ? "s" : ""} idêntico{data.common_blocks_count !== 1 ? "s" : ""}
                </span>
                <span className="text-[12px] text-amber-900">
                  <b>{data.divergences_count}</b> divergência{data.divergences_count !== 1 ? "s" : ""}
                </span>
                <span className="text-[12px] text-amber-900">
                  <b>{usedPlaceholders.size}</b> placeholder{usedPlaceholders.size !== 1 ? "s" : ""} extraído{usedPlaceholders.size !== 1 ? "s" : ""}
                </span>
              </div>

              {/* Título & tipo */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2">
                  <label className="text-[10.5px] uppercase tracking-widest text-slate-500">Título do template</label>
                  <input value={title} maxLength={200}
                    onChange={(e) => setTitle(e.target.value)}
                    data-testid="converge-title"
                    className="mt-1 w-full h-10 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"/>
                </div>
                <div>
                  <label className="text-[10.5px] uppercase tracking-widest text-slate-500">Tipo</label>
                  <select value={docType} onChange={(e) => setDocType(e.target.value)}
                    data-testid="converge-doctype"
                    className="mt-1 w-full h-10 px-3 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400">
                    {["Manifestação","Petição Inicial","Contestação","Recurso","Embargos","Mandado de Segurança","Outro"].map(t =>
                      <option key={t} value={t}>{t}</option>
                    )}
                  </select>
                </div>
              </div>

              {/* Variáveis */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <BookMarked className="w-4 h-4 text-slate-500"/>
                  <div className="text-[10.5px] uppercase tracking-widest text-slate-500">
                    Variáveis · {variables.filter(v => usedPlaceholders.has(v.name)).length} usada{variables.filter(v => usedPlaceholders.has(v.name)).length !== 1 ? "s" : ""}
                  </div>
                </div>
                {variables.length === 0 ? (
                  <div className="text-xs text-slate-500 italic border border-dashed border-slate-200 rounded-lg p-3 bg-slate-50">
                    Nenhuma variável extraída. As duas peças eram idênticas nos blocos comuns.
                  </div>
                ) : (
                  <div className="space-y-2" data-testid="converge-variables">
                    {variables.map((v, idx) => {
                      const inUse = usedPlaceholders.has(v.name);
                      return (
                        <div key={v.name} data-testid={`converge-var-${v.name}`}
                          className={`rounded-lg border p-2.5 ${inUse ? "border-amber-200 bg-amber-50/40" : "border-slate-200 bg-slate-50 opacity-60"}`}>
                          <div className="flex items-baseline gap-2">
                            <code className="font-mono text-[12px] font-bold text-amber-900 bg-amber-100 border border-amber-300 rounded px-1.5 py-0.5">
                              {`{{${v.name}}}`}
                            </code>
                            {!inUse && <span className="text-[10px] text-slate-500 italic">não usada no template</span>}
                          </div>
                          <input value={v.description || ""}
                            onChange={(e) => updateVarField(idx, "description", e.target.value)}
                            placeholder="Descrição semântica"
                            data-testid={`converge-var-desc-${v.name}`}
                            className="mt-1.5 w-full h-8 px-2 border border-slate-200 rounded text-[12.5px] bg-white focus:outline-none focus:border-amber-400"/>
                          {(v.examples || []).length > 0 && (
                            <div className="mt-1.5 flex flex-wrap gap-1 items-center">
                              <span className="text-[10px] text-slate-500 uppercase tracking-widest">Exemplos:</span>
                              {v.examples.slice(0, 3).map((ex, i) => (
                                <span key={i} className="text-[11px] px-1.5 py-0.5 bg-white border border-slate-200 rounded text-slate-700 font-mono">
                                  {String(ex).slice(0, 40)}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Template preview / editor */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="w-4 h-4 text-slate-500"/>
                  <div className="text-[10.5px] uppercase tracking-widest text-slate-500 flex-1">
                    Template com placeholders destacados
                  </div>
                  <button onClick={copyMarkdown} data-testid="converge-copy"
                    className="h-7 px-2.5 rounded border border-slate-200 bg-white text-slate-600 text-[11px] hover:border-slate-300 inline-flex items-center gap-1">
                    <Copy className="w-3 h-3"/> Copiar
                  </button>
                </div>
                <div className="rounded-xl border border-slate-200 overflow-hidden bg-white">
                  <div className="max-h-[40vh] overflow-y-auto p-4 text-[12.5px] leading-relaxed whitespace-pre-wrap font-mono text-slate-800"
                       data-testid="converge-preview">
                    {highlightedContent}
                  </div>
                  <textarea value={content}
                    onChange={(e) => setContent(e.target.value)}
                    data-testid="converge-content-edit"
                    className="w-full h-32 px-4 py-3 border-t border-slate-200 text-[12.5px] font-mono leading-relaxed focus:outline-none resize-none"
                    placeholder="Você pode ajustar o texto ou adicionar novos {{PLACEHOLDERS}}"/>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {!busy && !error && preview && (
          <div className="px-6 py-3 border-t border-slate-200 bg-white flex items-center gap-2 flex-wrap">
            <div className="text-[11px] text-slate-500 mr-auto">
              O template ficará disponível em <b>Modelos & Cláusulas</b> para futuras peças.
            </div>
            <button onClick={onClose} disabled={saving}
              className="h-10 px-4 rounded-lg border border-slate-200 text-slate-700 text-sm hover:border-slate-300">
              Cancelar
            </button>
            <button onClick={save} disabled={saving || !title.trim() || !content.trim()}
              data-testid="converge-save"
              className="h-10 px-5 rounded-lg text-white text-sm font-semibold inline-flex items-center gap-2 hover:opacity-95 disabled:opacity-50"
              style={{ background: NAVY }}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}
              Salvar como template
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

